import { PaymentsProvider } from '@zorro/clients';
import { Card } from '@zorro/design-system/v2/Card';
import { Group, Stack } from '@zorro/web/design-system';
import {
  EmployeePaymentStatus,
  LynxBankingDisclaimer,
  PaymentDetails,
  PaymentMethodViewer,
  useLynxPaymentsContext,
} from '@zorro/web/shared-ui';
import { useRoles } from '@zorro/web/ui-utils';
import { $enum } from 'ts-enum-util';

import { EnrollmentAction } from '../../../EmployeePageHeader';
import { ChangePaymentMethodStatus } from './ChangePaymentMethodStatus';
import { EmployeePaymentMethodDetails } from './EmployeePaymentMethodDetails';
import { EnrollmentPaymentMethod } from './EnrollmentPaymentMethod';
import { PaymentAmountDistribution } from './PaymentAmountDistribution';
import { PaymentMethodType } from './PaymentMethodType';

type Props = {
  onboardingPeriodId: string;
  employeeId: string;
  employerId: string;
  isDon: boolean;
  onEnrollmentTabActionChange: (action?: EnrollmentAction) => void;
};

export const EmployeePaymentsTab = ({
  onboardingPeriodId,
  employeeId,
  employerId,
  isDon,
  onEnrollmentTabActionChange,
}: Props) => {
  const { paymentsProvider, isAccountActive } = useLynxPaymentsContext({
    employerId,
    employeeId,
  });
  const { isOmnipotentAdmin, isLoading: isRolesLoading } = useRoles();

  const viewer = isOmnipotentAdmin
    ? PaymentMethodViewer.OPS
    : isDon
      ? PaymentMethodViewer.EMPLOYER_ADMIN
      : undefined;

  if (paymentsProvider === undefined || isRolesLoading) {
    return null;
  }

  return $enum.mapValue(paymentsProvider).with({
    [PaymentsProvider.LYNX]: (
      <Stack gap="1.5rem">
        {isAccountActive && viewer && (
          <Card variant="white-shadow" p="1.5rem">
            <PaymentDetails
              viewer={viewer}
              employeeId={employeeId}
              employerId={employerId}
              onboardingPeriodId={onboardingPeriodId}
            />
          </Card>
        )}
        <EmployeePaymentStatus
          onboardingPeriodId={onboardingPeriodId}
          shouldShowManageButton={!isDon}
        />
        {isAccountActive && <LynxBankingDisclaimer />}
      </Stack>
    ),
    [PaymentsProvider.ECHO]: (
      <Stack gap="xl">
        <Card variant="white-shadow" p="xl">
          <Stack gap="xl">
            <>
              <PaymentMethodType
                employeeId={employeeId}
                onboardingPeriodId={onboardingPeriodId}
              />
              <Stack>
                <PaymentAmountDistribution
                  employeeId={employeeId}
                  onboardingPeriodId={onboardingPeriodId}
                />
                <EmployeePaymentMethodDetails
                  employeeId={employeeId}
                  onboardingPeriodId={onboardingPeriodId}
                  withHeader
                />
              </Stack>
              {!isDon && (
                <Group align="end" gap="lg">
                  <ChangePaymentMethodStatus
                    employeeId={employeeId}
                    onboardingPeriodId={onboardingPeriodId}
                  />
                  <EnrollmentPaymentMethod
                    employeeId={employeeId}
                    onboardingPeriodId={onboardingPeriodId}
                    onEnrollmentTabActionChange={onEnrollmentTabActionChange}
                  />
                </Group>
              )}
            </>
          </Stack>
        </Card>
        <EmployeePaymentStatus
          onboardingPeriodId={onboardingPeriodId}
          shouldShowManageButton={!isDon}
        />
      </Stack>
    ),
  });
};
